import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, useTheme, useMediaQuery, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { HourlyForecastTimelineData, HourlyForecastTimelineSegment } from '../../models/HourlyForecastTimelineData';
import HourlyForecastTimelineDebugHover from './HourlyForecastTimelineDebugHover';
import { ALL_AIRPORTS_DATA } from '../../data/locations';
import { formatVisibility } from '../../utils/visibilityFormatters';
import { isOutsideOperatingHours } from '../../utils/operatingHours';

interface HourlyForecastTimelineProps {
  timelineData: HourlyForecastTimelineData;
  useUTCTime?: boolean;
  customNow?: Date | null;
  hideTitle?: boolean;
  hideLegend?: boolean;
  compact?: boolean;
  hideZoomIcon?: boolean;
}

// Interface for hover tooltip data
interface HoverInfo {
  time: Date;
  x: number;
  y: number;
  segment: HourlyForecastTimelineSegment | null;
  isPast: boolean;
}

const HourlyForecastTimeline: React.FC<HourlyForecastTimelineProps> = ({
  timelineData,
  useUTCTime = true,
  customNow = null,
  hideTitle = false,
  hideLegend = false,
  compact = false,
  hideZoomIcon = false
}) => {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  // Add state variable to track last hovered segment for JSON display
  const [lastHoveredSegment, setLastHoveredSegment] = useState<HourlyForecastTimelineSegment | null>(null);
  const [lastHoveredIsPast, setLastHoveredIsPast] = useState<boolean>(false);
  // Add state variable to force updates
  const [forceUpdate, setForceUpdate] = useState(0);
  // State to control debug hover visibility
  const [debugHoverVisible, setDebugHoverVisible] = useState(true);

  // Check if debug mode is enabled in the query string
  const isDebugMode = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === 'true';

  // Helper function to format the airport name with "Airport" suffix
  const getAirportName = (airportName: string): string => {
    // If the name already contains "Airport", return as-is
    if (airportName.includes('Airport')) {
      return airportName;
    }
    // Otherwise add "Airport" suffix
    return `${airportName} Airport`;
  };

  // Helper function to get ICAO code from location ID
  const getICAOCode = (locationId: number): string | undefined => {
    const airport = ALL_AIRPORTS_DATA.find(a => a.id === locationId);
    return airport?.code;
  };

  // Define color constants
  const FOG_800M_COLOR = "#d32f2f"; // Red for visibility ≤ 800m
  const FOG_1600M_COLOR = "#f57c00"; // Orange for visibility ≤ 1600m (darker)
  const FOG_3000M_COLOR = "#ffd54f"; // Yellow for visibility ≤ 3000m (slightly darker)
  const NO_FOG_COLOR = "#4caf50"; // Green for no fog
  const NO_DATA_COLOR = "#757575"; // Gray for no data
  const OUT_OF_HOURS_COLOR = "#4a4a4a"; // Dark gray for out of operating hours
  const NOW_LINE_COLOR = "#ba68c8"; // Purple for "now" line

  // Helper functions for timezone-aware time methods
  const getHours = (date: Date): number => {
    return useUTCTime ? date.getUTCHours() : date.getHours();
  };

  const getMinutes = (date: Date): number => {
    return useUTCTime ? date.getUTCMinutes() : date.getMinutes();
  };

  const formatTime = (date: Date): string => {
    const hours = getHours(date);
    const minutes = getMinutes(date);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Actuals use segment_interval (30 or 60 min), predictions always use 60-min
  const actualsIntervalMinutes = timelineData.segment_interval || 60;

  // Function to format time as a range (30-min for actuals, 60-min for predictions)
  const formatTimeRange = (startTime: Date, isPast?: boolean): string => {
    // Create a copy of the start time
    const endTime = new Date(startTime);

    // Use 30-min for past actuals (if segment_interval is 30), 60-min for predictions
    const intervalMinutes = isPast && actualsIntervalMinutes === 30 ? 30 : 60;

    // Add interval minutes to get the end time
    if (useUTCTime) {
      endTime.setUTCMinutes(getMinutes(endTime) + intervalMinutes - 1, 59); // End at the last second of the segment
    } else {
      endTime.setMinutes(getMinutes(endTime) + intervalMinutes - 1, 59); // End at the last second of the segment
    }

    // Format both times
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  // Helper to check if a segment has the appropriate data for its position
  const hasRelevantDataForPosition = (segment: HourlyForecastTimelineSegment | null, time?: Date): boolean => {
    if (!segment) return false;

    // Get reference time - this should NOT use the time parameter or we'll compare a time with itself
    // If customNow exists, use it directly; otherwise create appropriate date based on useUTCTime
    const referenceTime = customNow ||
      (useUTCTime ? new Date(new Date().toISOString()) : new Date());

    // Check if segment is in the past or future
    const isPastSegment = time ? isInPast(time, referenceTime) : false;

    // For past segments (left of now line), we should have actuals data
    // For future segments (right of now line), we should have predictions data
    return isPastSegment
      ? (segment.raw_actuals !== null && segment.raw_actuals !== undefined &&
         segment.raw_actuals.fog_actuals !== undefined && segment.raw_actuals.fog_actuals !== null &&
         Array.isArray(segment.raw_actuals.fog_actuals) && segment.raw_actuals.fog_actuals.length > 0)
      : (segment.raw_predictions !== null && segment.raw_predictions !== undefined &&
         segment.raw_predictions.predictions !== undefined && segment.raw_predictions.predictions !== null &&
         Array.isArray(segment.raw_predictions.predictions) && segment.raw_predictions.predictions.length > 0);
  };

  // Helper to check if a time is outside operating hours for this airport
  const isSegmentOutsideOperatingHours = (time: Date): boolean => {
    if (!timelineData.operatingHours) return false;
    return isOutsideOperatingHours(time, timelineData.operatingHours);
  };

  // Helper to get color based on segment data
  const getSegmentColor = (segment: HourlyForecastTimelineSegment | null, time?: Date): string => {
    // Return gray for no segment
    if (!segment) return NO_DATA_COLOR;

    // Return out-of-hours color if outside operating hours and no data
    if (!hasRelevantDataForPosition(segment, time)) {
      if (time && isSegmentOutsideOperatingHours(time)) return OUT_OF_HOURS_COLOR;
      return NO_DATA_COLOR;
    }

    // Color is determined by visibility_level when available
    if (segment.visibility_level != null) {
      if (segment.visibility_level <= 800) {
        return FOG_800M_COLOR;  // Red
      } else if (segment.visibility_level <= 1600) {
        return FOG_1600M_COLOR; // Orange
      } else if (segment.visibility_level <= 3000) {
        return FOG_3000M_COLOR; // Yellow
      } else {
        return NO_FOG_COLOR;    // Green - visibility > 3000
      }
    }

    // No visibility level available - fall back to fog_predicted
    return segment.fog_predicted ? NO_DATA_COLOR : NO_FOG_COLOR;
  };

  // Helper to get formatted visibility label
  const getVisibilityLabel = (segment: HourlyForecastTimelineSegment | null, time?: Date): string => {
    // Return "No Data" for no segment
    if (!segment) return "No Data";

    // Return appropriate label if no relevant data for this position
    if (!hasRelevantDataForPosition(segment, time)) {
      if (time && isSegmentOutsideOperatingHours(time)) return "Out of operational hours";
      return "No Data";
    }

    // Label is determined by visibility_level when available
    if (segment.visibility_level != null) {
      if (segment.visibility_level <= 800) {
        return `≤ ${formatVisibility(800)}`;
      } else if (segment.visibility_level <= 1600) {
        return `≤ ${formatVisibility(1600)}`;
      } else if (segment.visibility_level <= 3000) {
        return `≤ ${formatVisibility(3000)}`;
      } else {
        return `> ${formatVisibility(3000)}`;
      }
    }

    // No visibility level available - fall back to fog_predicted
    return segment.fog_predicted ? "No Data" : `> ${formatVisibility(3000)}`;
  };

  // Define function to determine if a time is in the past or future relative to now
  const isInPast = (time: Date, now: Date): boolean => {
    // The timeline is divided at the last 15-minute mark
    // All segments before this mark are "actuals"
    // Segments at or after this mark are "forecast"

    // Calculate divider time (rounded down to nearest 15 min)
    const currentTime = new Date(now);
    const minutes = getMinutes(currentTime);
    const dividerMinutes = Math.floor(minutes / 15) * 15;

    // Create a date for the divider time
    const dividerTime = new Date(currentTime);
    if (useUTCTime) {
      dividerTime.setUTCMinutes(dividerMinutes);
      dividerTime.setUTCSeconds(0);
      dividerTime.setUTCMilliseconds(0);
    } else {
      dividerTime.setMinutes(dividerMinutes);
      dividerTime.setSeconds(0);
      dividerTime.setMilliseconds(0);
    }

    // A time is in the past if it is strictly less than the divider time
    return time.getTime() < dividerTime.getTime();
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isDesktopLike = !isMobile || (isMobile && isLandscape);
  const isMobileLandscape = isMobile && isLandscape;
  // For mobile layout logic
  const isMobilePortrait = isMobile && !isLandscape;
  const isTabletPortrait = useMediaQuery(theme.breakpoints.between('sm', 'md')) && !isLandscape;

  useEffect(() => {
    if (!svgRef.current) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Constants for graph dimensions and styling
    const margin = { top: 28, right: 60, bottom: 22, left: 60 };
    const height = 40;
    // Predictions always use 60-min intervals (actualsIntervalMinutes defined at component level)
    const predictionsIntervalMinutes = 60;
    // Cell widths: 64px for 30-min, 128px for 60-min
    const actualsCellWidth = (128 * actualsIntervalMinutes) / 60;
    const predictionsCellWidth = 128;
    const lineExtension = Math.floor(height / 3);
    const nowLineWidth = 3;
    const borderColor = 'rgba(255, 255, 255, 0.3)';

    // Use custom time if provided, otherwise create appropriate date based on useUTCTime setting
    const now = customNow || (useUTCTime ? new Date(new Date().toISOString()) : new Date());

    // Determine the center point for the timeline
    // Center line is at the last 15-minute mark, while hourly blocks align from there
    const centerPoint = new Date(now);
    // Get current minutes
    const minutes = getMinutes(centerPoint);
    // Calculate target minutes as the last 15-minute mark (e.g., 58 → 45, 32 → 30, 12 → 0)
    const targetMinutes = Math.floor(minutes / 15) * 15;

    // Set the center point minutes
    if (useUTCTime) {
      centerPoint.setUTCMinutes(targetMinutes);
      centerPoint.setUTCSeconds(0);
      centerPoint.setUTCMilliseconds(0);
    } else {
      centerPoint.setMinutes(targetMinutes);
      centerPoint.setSeconds(0);
      centerPoint.setMilliseconds(0);
    }

    // Use the calculated center point as the closest hour marker for consistency
    const closestHourMarker = new Date(centerPoint);

    // Calculate start time (4 hours before closest hour marker)
    const startTime = new Date(closestHourMarker);
    if (useUTCTime) {
      startTime.setUTCHours(getHours(closestHourMarker) - 4);
    } else {
      startTime.setHours(getHours(closestHourMarker) - 4);
    }

    // Calculate end time (4 hours after closest hour marker)
    const endTime = new Date(closestHourMarker);
    if (useUTCTime) {
      endTime.setUTCHours(getHours(closestHourMarker) + 4);
    } else {
      endTime.setHours(getHours(closestHourMarker) + 4);
    }

    // Calculate number of increments for each section (4 hours each)
    // Actuals (past): 30-min = 8 increments, 60-min = 4 increments
    // Predictions (future): always 4 increments (60-min each)
    const numActualsIncrements = (4 * 60) / actualsIntervalMinutes;
    const numPredictionsIncrements = 4;

    // Calculate total width: actuals section + predictions section
    const actualsWidth = actualsCellWidth * numActualsIncrements;
    const predictionsWidth = predictionsCellWidth * numPredictionsIncrements;
    const width = actualsWidth + predictionsWidth + margin.left + margin.right;

    // Create SVG and set dimensions
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${width} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Add pattern definition for out-of-hours diagonal stripes
    const defs = svg.append('defs');
    const pattern = defs.append('pattern')
      .attr('id', `out-of-hours-pattern-${timelineData.location_id}`)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 8)
      .attr('height', 8)
      .attr('patternTransform', 'rotate(45)');
    pattern.append('rect')
      .attr('width', 8)
      .attr('height', 8)
      .attr('fill', OUT_OF_HOURS_COLOR);
    pattern.append('line')
      .attr('x1', 0).attr('y1', 0)
      .attr('x2', 0).attr('y2', 8)
      .attr('stroke', '#666666')
      .attr('stroke-width', 4);

    // Create a custom position function since we have different cell widths
    // Actuals (past) use actualsIntervalMinutes, predictions (future) use 60-min
    const getXPosition = (time: Date): number => {
      const timeMs = time.getTime();
      const centerMs = closestHourMarker.getTime();
      const startMs = startTime.getTime();

      if (timeMs < centerMs) {
        // Past (actuals) - use actuals cell width
        const minutesFromStart = (timeMs - startMs) / (1000 * 60);
        return (minutesFromStart / actualsIntervalMinutes) * actualsCellWidth;
      } else {
        // Future (predictions) - use predictions cell width
        const minutesFromCenter = (timeMs - centerMs) / (1000 * 60);
        return actualsWidth + (minutesFromCenter / predictionsIntervalMinutes) * predictionsCellWidth;
      }
    };

    // Generate time increments for actuals (past) with actualsIntervalMinutes
    const actualsIncrements: Date[] = [];
    let currentTime = new Date(startTime);
    for (let i = 0; i < numActualsIncrements; i++) {
      actualsIncrements.push(new Date(currentTime));
      if (useUTCTime) {
        currentTime.setUTCMinutes(getMinutes(currentTime) + actualsIntervalMinutes);
      } else {
        currentTime.setMinutes(getMinutes(currentTime) + actualsIntervalMinutes);
      }
    }

    // Generate time increments for predictions (future) with 60-min intervals
    const predictionsIncrements: Date[] = [];
    currentTime = new Date(closestHourMarker);
    for (let i = 0; i < numPredictionsIncrements; i++) {
      predictionsIncrements.push(new Date(currentTime));
      if (useUTCTime) {
        currentTime.setUTCMinutes(getMinutes(currentTime) + predictionsIntervalMinutes);
      } else {
        currentTime.setMinutes(getMinutes(currentTime) + predictionsIntervalMinutes);
      }
    }

    // Combined time increments for iteration
    const timeIncrements = [...actualsIncrements, ...predictionsIncrements];

    // Legacy timeScale for hour markers (uses linear interpolation across full range)
    const timeScale = d3.scaleTime()
      .domain([startTime, endTime])
      .range([0, actualsWidth + predictionsWidth]);

    // Helper function to find corresponding segment for a time
    const findSegmentForTime = (time: Date, isPast: boolean): HourlyForecastTimelineSegment | undefined => {
      if (!timelineData || !timelineData.segments || timelineData.segments.length === 0) {
        return undefined;
      }

      // Find the segment by matching the time AND segment type (actuals vs predictions)
      return timelineData.segments.find(segment => {
        if (!segment.time) return false;

        // Filter by segment type: actuals have raw_actuals, predictions have raw_predictions
        if (isPast && !segment.raw_actuals) return false;
        if (!isPast && !segment.raw_predictions) return false;

        const segmentTime = new Date(segment.time);

        if (isPast && actualsIntervalMinutes === 30) {
          // For 30-min actuals, find closest match within ±15 minutes
          // METAR data is at :00/:30 but timeline increments may start from :15/:45
          const timeDiff = Math.abs(segmentTime.getTime() - time.getTime());
          const halfInterval = (actualsIntervalMinutes / 2) * 60 * 1000;
          return timeDiff <= halfInterval;
        } else {
          // For 60-min predictions (or 60-min actuals), match by hour only
          const timeMatches = useUTCTime
            ? segmentTime.getUTCHours() === time.getUTCHours()
            : segmentTime.getHours() === time.getHours();
          return timeMatches;
        }
      });
    };

    // Generate hour markers
    const hourMarkers: Date[] = [];
    for (let i = -4; i <= 4; i++) {
      const marker = new Date(closestHourMarker);
      if (useUTCTime) {
        marker.setUTCHours(getHours(closestHourMarker) + i);
      } else {
        marker.setHours(getHours(closestHourMarker) + i);
      }
      hourMarkers.push(marker);
    }

    // Draw timeline cells - actuals (past) with actualsIntervalMinutes, predictions (future) with 60-min
    // Helper to determine fill value (solid color or diagonal stripe pattern for out-of-hours)
    const getCellFill = (segment: HourlyForecastTimelineSegment | undefined | null, time: Date): string => {
      const isOOH = isSegmentOutsideOperatingHours(time) && !hasRelevantDataForPosition(segment || null, time);
      if (isOOH) return `url(#out-of-hours-pattern-${timelineData.location_id})`;
      return getSegmentColor(segment || null, time);
    };

    // Draw actuals cells
    actualsIncrements.forEach((time, i) => {
      const segment = findSegmentForTime(time, true);

      svg.append('rect')
        .attr('class', 'time-cell')
        .attr('data-index', i)
        .attr('x', getXPosition(time))
        .attr('y', 0)
        .attr('width', actualsCellWidth)
        .attr('height', height)
        .attr('fill', getCellFill(segment, time));
    });

    // Draw predictions cells
    predictionsIncrements.forEach((time, i) => {
      const segment = findSegmentForTime(time, false);

      svg.append('rect')
        .attr('class', 'time-cell')
        .attr('data-index', numActualsIncrements + i)
        .attr('x', getXPosition(time))
        .attr('y', 0)
        .attr('width', predictionsCellWidth)
        .attr('height', height)
        .attr('fill', getCellFill(segment, time));
    });

    // Add borders between 60-minute increments
    svg.selectAll('.time-increment-border')
      .data(timeIncrements)
      .enter()
      .append('line')
      .attr('class', 'time-increment-border')
      .attr('x1', time => timeScale(time))
      .attr('y1', 0)
      .attr('x2', time => timeScale(time))
      .attr('y2', height)
      .attr('stroke', borderColor)
      .attr('stroke-width', 1);

    // Add cell borders for highlighting on hover
    svg.selectAll('.cell-border')
      .data(timeIncrements)
      .enter()
      .append('rect')
      .attr('class', 'cell-border')
      .attr('data-index', (_d, i) => i)
      .attr('x', time => getXPosition(time))
      .attr('y', 0)
      .attr('width', (_d, i) => i < numActualsIncrements ? actualsCellWidth : predictionsCellWidth)
      .attr('height', height)
      .attr('fill', 'none')
      .attr('stroke', borderColor)
      .attr('stroke-width', 1);

    // Draw vertical lines for hour markers (drawn after cells so they appear on top)
    svg.selectAll('.hour-marker')
      .data(hourMarkers)
      .enter()
      .append('line')
      .attr('class', 'hour-marker')
      .attr('x1', d => timeScale(d))
      .attr('y1', -lineExtension)
      .attr('x2', d => timeScale(d))
      .attr('y2', height + lineExtension)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', d => {
        // Make the center hour marker (closest to the current time) bolder
        return d.getTime() === closestHourMarker.getTime() ? 2.5 : 1.5;
      })
      .attr('stroke-opacity', d => {
        // Make the center hour marker more opaque
        return d.getTime() === closestHourMarker.getTime() ? 1 : 0.8;
      });

    // Format the time string for the Now label
    const timeString = `Now - ${formatTime(now)}`;

    // Create a temporary text element to calculate the width of the text
    const tempText = svg.append('text')
      .attr('class', 'temp-text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .style('visibility', 'hidden')
      .text(timeString);

    // Get the bounding box of the text to size the background rectangle
    const textBBox = tempText.node()?.getBBox() || { width: 100, height: 20 };
    tempText.remove(); // Remove the temporary text

    // Calculate dimensions and positions for the Now label
    const labelPadding = { x: 6, y: 4 };
    const labelHeight = textBBox.height + labelPadding.y * 2;
    const labelWidth = textBBox.width + labelPadding.x * 2;

    // Y-position for the label background
    const labelY = -lineExtension - 30;

    // Position "Now" line at the exact current time
    const nowPosition = timeScale(now);

    // Add background rectangle for "Now" label
    svg.append('rect')
      .attr('class', 'now-label-bg')
      .attr('x', nowPosition - (labelWidth / 2))
      .attr('y', labelY)
      .attr('width', labelWidth)
      .attr('height', labelHeight)
      .attr('rx', labelHeight / 2)
      .attr('ry', labelHeight / 2)
      .attr('fill', NOW_LINE_COLOR);

    // Add "Now" text
    svg.append('text')
      .attr('class', 'now-label')
      .attr('x', nowPosition)
      .attr('y', labelY + (labelHeight / 2))
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .attr('dominant-baseline', 'central')
      .text(timeString);

    // First, add a glow effect with a wider stroke
    svg.append('line')
      .attr('class', 'now-line-glow')
      .attr('x1', nowPosition)
      .attr('y1', labelY + labelHeight)
      .attr('x2', nowPosition)
      .attr('y2', height + lineExtension)
      .attr('stroke', NOW_LINE_COLOR)
      .attr('stroke-width', nowLineWidth + 3)
      .attr('stroke-opacity', 0.3)
      .attr('stroke-linecap', 'round');

    // Then add the main "Now" line
    svg.append('line')
      .attr('class', 'now-line')
      .attr('x1', nowPosition)
      .attr('y1', labelY + labelHeight)
      .attr('x2', nowPosition)
      .attr('y2', height + lineExtension)
      .attr('stroke', NOW_LINE_COLOR)
      .attr('stroke-width', nowLineWidth)
      .attr('stroke-linecap', 'round');

    // Draw hour labels
    svg.selectAll('.hour-label')
      .data(hourMarkers)
      .enter()
      .append('text')
      .attr('class', 'hour-label')
      .attr('x', d => timeScale(d))
      .attr('y', height + lineExtension + 18)
      .attr('text-anchor', 'middle')
      .attr('fill', '#ffffff')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .attr('opacity', 1)
      .text(time => formatTime(time));

    // Add "Actuals" label on the left
    svg.append('text')
      .attr('class', 'timeline-section-label')
      .attr('x', timeScale(hourMarkers[0])) // Align with first hour marker
      .attr('y', -30) // Position closer to the timeline
      .attr('text-anchor', 'middle')
      .attr('fill', '#d0d0d0') // Lighter color
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text('Actuals');

    // Add "Forecast" label on the right
    svg.append('text')
      .attr('class', 'timeline-section-label')
      .attr('x', timeScale(hourMarkers[hourMarkers.length - 1])) // Align with last hour marker
      .attr('y', -30) // Position closer to the timeline
      .attr('text-anchor', 'middle')
      .attr('fill', '#d0d0d0') // Lighter color
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text('Forecast');

    // Add invisible hover areas for each time increment
    svg.selectAll('.hover-area')
      .data(timeIncrements)
      .enter()
      .append('rect')
      .attr('class', 'hover-area')
      .attr('data-index', (_d, i) => i)
      .attr('x', time => getXPosition(time))
      .attr('y', -lineExtension)
      .attr('width', (_d, i) => i < numActualsIncrements ? actualsCellWidth : predictionsCellWidth)
      .attr('height', height + lineExtension * 2)
      .attr('fill', 'transparent')
      .attr('cursor', 'pointer')
      .on('mouseenter', function(event, time) {
        // Get the index from the data attribute
        const index = d3.select(this).attr('data-index');

        // Highlight only this specific cell border
        svg.selectAll('.cell-border')
          .attr('stroke', borderColor)
          .attr('stroke-width', 1)
          .attr('stroke-opacity', 1);

        // Then highlight only the current cell
        d3.select(this.parentElement)
          .select(`.cell-border[data-index='${index}']`)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 3)
          .attr('stroke-opacity', 1);

        // Get segment data for this time
        const isPast = isInPast(time, now);
        const segment = findSegmentForTime(time, isPast);

        // Only update last hovered segment if debug mode is enabled (for efficiency)
        if (isDebugMode) {
          setLastHoveredSegment(segment || null);
          setLastHoveredIsPast(isPast);
          setDebugHoverVisible(true); // Show debug hover again on hover
        }

        // Get SVG container bounds for proper tooltip positioning
        const containerBounds = containerRef.current?.getBoundingClientRect();

        // Create hover info with relative coordinates if container exists
        if (containerBounds) {
          setHoverInfo({
            time,
            x: event.clientX - containerBounds.left,
            y: event.clientY - containerBounds.top,
            segment: segment || null,
            isPast
          });
        }
      })
      .on('mouseleave', function() {
        // Reset cell borders
        svg.selectAll('.cell-border')
          .attr('stroke', borderColor)
          .attr('stroke-width', 1)
          .attr('stroke-opacity', 1);

        // Clear hover info
        setHoverInfo(null);
      });

    // Set up an interval to update the graph every minute
    // This ensures the timeline shifts as time progresses
    const updateInterval = setInterval(() => {
      // Only update if the component is still mounted
      if (svgRef.current) {
        // Force a re-render by updating a state variable
        setForceUpdate(prev => prev + 1);
      }
    }, 60000); // Update every minute

    // Clean up the interval when the component unmounts
    return () => clearInterval(updateInterval);
  }, [timelineData, useUTCTime, forceUpdate, customNow]);

  // Helper to get color for a visibility value (used for mean/median boxes)
  function getVisibilityColor(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) return NO_DATA_COLOR;
    if (value <= 800) return FOG_800M_COLOR;
    if (value <= 1600) return FOG_1600M_COLOR;
    if (value <= 3000) return FOG_3000M_COLOR;
    return NO_FOG_COLOR;
  }

  return (
    <Box sx={{ width: '100%', mb: compact ? 0 : 0 }}>
      {/* SVG Container with title, legend, and timeline */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          overflowX: 'auto',
          backgroundColor: 'background.paper',
          borderRadius: compact ? 0 : 1,
          display: 'flex',
          flexDirection: (isDesktopLike && !isMobileLandscape) ? 'row' : 'column',
          alignItems: (isDesktopLike && !isMobileLandscape) ? 'stretch' : 'flex-start',
          padding: compact ? 0.5 : 1,
          mb: compact ? 0 : 0
        }}
      >
        {/* Title and horizontal legend for mobile portrait and mobile landscape */}
        {(!hideTitle && (!isDesktopLike || isMobileLandscape)) ? (
          <Box sx={{ width: '100%', mb: 2 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', fontSize: isTabletPortrait ? '1.1rem' : { xs: '1.25rem', sm: '1.75rem' } }}>
              {timelineData?.location_name ? getAirportName(timelineData.location_name) : "Unknown Location"}
            </Typography>
            {timelineData?.location_id && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: -0.5 }}>
                <Typography variant="body1" sx={{ opacity: 0.8, fontWeight: 500, fontSize: isTabletPortrait ? '0.95rem' : { xs: '1rem', sm: '1.25rem' } }}>
                  {getICAOCode(timelineData.location_id)}
                </Typography>
                {!hideZoomIcon && (
                  <Tooltip title="View this timeline by itself" placement="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/location/${timelineData.location_id}${customNow ? `?time=${customNow.toISOString()}` : ''}`)}
                      sx={{
                        ml: 0.5,
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            {/* Horizontal legend - only show if hideLegend is false */}
            {!hideLegend && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                mt: 2,
                mb: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                padding: 1,
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', width: '100%', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 90, mb: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: FOG_800M_COLOR, mr: 0.7, borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`≤ ${formatVisibility(800)}`}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 90, mb: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: FOG_1600M_COLOR, mr: 0.7, borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`≤ ${formatVisibility(1600)}`}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 90, mb: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: FOG_3000M_COLOR, mr: 0.7, borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`≤ ${formatVisibility(3000)}`}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 90, mb: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: NO_FOG_COLOR, mr: 0.7, borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`> ${formatVisibility(3000)}`}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 90, mb: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: NO_DATA_COLOR, mr: 0.7, borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>No data</Typography>
                  </Box>
                  {timelineData.operatingHours && (
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 90, mb: 0.5 }}>
                      <Box sx={{
                        width: 16, height: 16, mr: 0.7, borderRadius: 0.5,
                        background: `repeating-linear-gradient(45deg, ${OUT_OF_HOURS_COLOR}, ${OUT_OF_HOURS_COLOR} 2px, #666666 2px, #666666 4px)`
                      }} />
                      <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>Closed</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        ) : (!hideTitle && (isDesktopLike && !isMobileLandscape)) ? (
          // Desktop/landscape: keep left title
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 110,
              mr: 3,
              mb: 0,
              flex: '0 0 auto',
              width: 110,
              maxWidth: 110,
              alignItems: undefined
            }}
          >
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {timelineData?.location_name ? getAirportName(timelineData.location_name) : "Unknown Location"}
            </Typography>
            {timelineData?.location_id && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: -0.5 }}>
                <Typography variant="body1" sx={{ opacity: 0.8, fontWeight: 500, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  {getICAOCode(timelineData.location_id)}
                </Typography>
                {!hideZoomIcon && (
                  <Tooltip title="View this timeline by itself" placement="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/location/${timelineData.location_id}${customNow ? `?time=${customNow.toISOString()}` : ''}`)}
                      sx={{
                        ml: 0.5,
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        ) : null}
        {/* Center: Timeline SVG */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            mt: isMobilePortrait ? 0.5 : (isDesktopLike && !isMobileLandscape) ? 0 : -1,
            mb: isMobilePortrait ? 0.5 : (isDesktopLike && !isMobileLandscape) ? 0 : 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', minWidth: 0 }}>
            <svg
              ref={svgRef}
              style={{
                width: '100%',
                height: compact
                  ? (typeof window !== 'undefined' && window.innerWidth >= 3840 ? '180px' :
                     typeof window !== 'undefined' && window.innerWidth >= 2560 ? '150px' : '130px')
                  : (isMobilePortrait ? '90px' : isMobileLandscape ? '100px' : '140px'),
                minWidth: 0
              }}
            />
          </Box>
        </Box>
        {/* Right: Legend (desktop/landscape only) */}
        {!hideLegend && (isDesktopLike && !isMobileLandscape) && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            padding: 2,
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: 110,
            maxWidth: 160,
            ml: 1,
            alignSelf: 'center',
            minHeight: '240px',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '16px', opacity: 0.9 }}>
              Visibility
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 18, height: 18, backgroundColor: FOG_800M_COLOR, mr: 1 }} />
              <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`≤ ${formatVisibility(800)}`}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 18, height: 18, backgroundColor: FOG_1600M_COLOR, mr: 1 }} />
              <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`≤ ${formatVisibility(1600)}`}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 18, height: 18, backgroundColor: FOG_3000M_COLOR, mr: 1 }} />
              <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`≤ ${formatVisibility(3000)}`}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 18, height: 18, backgroundColor: NO_FOG_COLOR, mr: 1 }} />
              <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>{`> ${formatVisibility(3000)}`}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 18, height: 18, backgroundColor: NO_DATA_COLOR, mr: 1 }} />
              <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>No data</Typography>
            </Box>
            {timelineData.operatingHours && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                  width: 18, height: 18, mr: 1,
                  background: `repeating-linear-gradient(45deg, ${OUT_OF_HOURS_COLOR}, ${OUT_OF_HOURS_COLOR} 2px, #666666 2px, #666666 4px)`
                }} />
                <Typography variant="caption" sx={{ fontSize: { xs: '14px', md: '16px' }, opacity: 0.8 }}>Closed</Typography>
              </Box>
            )}
          </Box>
        )}
        {/* Tooltip - now using absolute positioning relative to container */}
        {hoverInfo && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              left: `${Math.min(hoverInfo.x + 15, containerRef.current?.clientWidth ? containerRef.current.clientWidth - 200 : hoverInfo.x)}px`,
              top: `${Math.max(hoverInfo.y - 100, 0)}px`,
              padding: 1.5,
              minWidth: 200,
              zIndex: 1000,
              backgroundColor: 'rgba(30, 30, 30, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
              {hoverInfo.isPast ? 'Actuals for ' : 'Forecast for '}{formatTimeRange(hoverInfo.time, hoverInfo.isPast)}
            </Typography>
            {hoverInfo.segment && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {/* Visibility level (current) - only for forecast */}
                {!hoverInfo.isPast && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: getSegmentColor(hoverInfo.segment, hoverInfo.time),
                        mr: 1,
                        borderRadius: 0.5,
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Visibility level: {getVisibilityLabel(hoverInfo.segment, hoverInfo.time)}
                    </Typography>
                  </Box>
                )}
                {/* Only show these for actuals (isPast) */}
                {hoverInfo.isPast && <>
                  {/* Harmonic mean visibility (displayed) */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: getVisibilityColor(hoverInfo.segment.raw_actuals?.visibility_hmean),
                        mr: 1,
                        borderRadius: 0.5,
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Harmonic mean: {formatVisibility(hoverInfo.segment.raw_actuals?.visibility_hmean)} (displayed)
                    </Typography>
                  </Box>
                  {/* Median visibility */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: getVisibilityColor(hoverInfo.segment.raw_actuals?.visibility_median),
                        mr: 1,
                        borderRadius: 0.5,
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Median: {formatVisibility(hoverInfo.segment.raw_actuals?.visibility_median)}
                    </Typography>
                  </Box>
                  {/* Range (lowest - highest) */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: getVisibilityColor(hoverInfo.segment.lowest_visibility_level),
                        mr: 1,
                        borderRadius: 0.5,
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Range: {formatVisibility(hoverInfo.segment.lowest_visibility_level)} - {formatVisibility(hoverInfo.segment.highest_visibility_level)}
                    </Typography>
                  </Box>
                </>}
              </Box>
            )}
          </Paper>
        )}
      </Box>
      {/* JSON Data Display - only shown in debug mode */}
      {isDebugMode && (
        <HourlyForecastTimelineDebugHover
          lastHoveredSegment={lastHoveredSegment}
          formatTimeRange={formatTimeRange}
          isInPast={isInPast}
          hoveredIsPast={lastHoveredIsPast}
          visible={debugHoverVisible}
          onDismiss={() => setDebugHoverVisible(false)}
        />
      )}
    </Box>
  );
};

export default HourlyForecastTimeline;
